/**
 * FibonacciTable - Fibonacci Levels Display
 *
 * Simple text-based table showing Fibonacci retracement levels
 * and current price position relative to each level.
 */

import React, { memo } from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Table row component - Institutional Grade Styling
 */
const TableRow = ({ label, value, valueColor = '#fff', highlight = false }) => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '6px 12px',
            borderBottom: '1px solid rgba(55, 65, 81, 0.5)',
            backgroundColor: highlight ? 'rgba(251, 191, 36, 0.1)' : 'transparent',
            '&:last-child': { borderBottom: 'none' },
            '&:hover': { backgroundColor: highlight ? 'rgba(251, 191, 36, 0.15)' : 'rgba(55, 65, 81, 0.25)' }
        }}
    >
        <Typography sx={{ fontSize: '13px', color: '#b4bcd0', fontFamily: 'monospace', fontWeight: 500 }}>
            {label}
        </Typography>
        <Typography sx={{ fontSize: '13px', color: valueColor, fontWeight: 700, fontFamily: 'monospace' }}>
            {value}
        </Typography>
    </Box>
);

/**
 * Fib level row with status indicator - Institutional Grade Styling
 */
const FibLevelRow = ({ level, price, status, isNearest = false, currentPrice }) => {
    const getStatusColor = (status) => {
        if (!status) return '#9ca3af';
        const s = status.toUpperCase();
        if (s === 'ABOVE') return '#22c55e';
        if (s === 'BELOW') return '#ef4444';
        if (s === 'AT') return '#fbbf24';
        return '#9ca3af';
    };

    const getStatusIcon = (status) => {
        if (!status) return '';
        const s = status.toUpperCase();
        if (s === 'ABOVE') return '↑';
        if (s === 'BELOW') return '↓';
        if (s === 'AT') return '→';
        return '';
    };

    // Calculate distance to level
    const distance = currentPrice && price ? ((currentPrice - price) / price * 100).toFixed(2) : null;

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '7px 12px',
                borderBottom: '1px solid rgba(55, 65, 81, 0.5)',
                backgroundColor: isNearest ? 'rgba(251, 191, 36, 0.12)' : 'transparent',
                borderLeft: isNearest ? '3px solid #fbbf24' : '3px solid transparent',
                '&:last-child': { borderBottom: 'none' },
                '&:hover': { backgroundColor: 'rgba(55, 65, 81, 0.25)' }
            }}
        >
            {/* Level */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: '70px' }}>
                <Typography
                    sx={{
                        fontSize: '13px',
                        color: isNearest ? '#fbbf24' : '#b4bcd0',
                        fontWeight: isNearest ? 700 : 500,
                        fontFamily: 'monospace'
                    }}
                >
                    {level}
                </Typography>
                {isNearest && (
                    <Typography sx={{ fontSize: '10px', color: '#fbbf24', fontWeight: 700 }}>
                        NEAREST
                    </Typography>
                )}
            </Box>

            {/* Price */}
            <Typography sx={{ fontSize: '13px', color: '#fff', fontFamily: 'monospace', minWidth: '80px', textAlign: 'right', fontWeight: 600 }}>
                {typeof price === 'number' ? price.toFixed(2) : price || '--'}
            </Typography>

            {/* Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: '70px', justifyContent: 'flex-end' }}>
                <Typography sx={{ fontSize: '12px', color: getStatusColor(status), fontWeight: 700, fontFamily: 'monospace' }}>
                    {getStatusIcon(status)} {status || '--'}
                </Typography>
            </Box>

            {/* Distance */}
            {distance !== null && (
                <Typography sx={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace', minWidth: '55px', textAlign: 'right' }}>
                    {distance > 0 ? '+' : ''}{distance}%
                </Typography>
            )}
        </Box>
    );
};

/**
 * Section header - Institutional Grade Styling
 */
const SectionHeader = ({ title, badge = null }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            backgroundColor: 'rgba(220, 38, 38, 0.12)',
            borderBottom: '1px solid rgba(220, 38, 38, 0.4)'
        }}
    >
        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#ef4444', letterSpacing: '0.8px' }}>
            {title}
        </Typography>
        {badge && (
            <Typography sx={{ fontSize: '11px', color: '#fbbf24', fontWeight: 700 }}>
                {badge}
            </Typography>
        )}
    </Box>
);

/**
 * FibonacciTable Component
 */
const FibonacciTable = memo(({ data, currentPrice }) => {
    // Extract data with fallbacks
    const swingHigh = data?.swingHigh;
    const swingLow = data?.swingLow;
    const levels = data?.levels || [];
    const nearestLevel = data?.nearestLevel;
    const distanceToNearest = data?.distanceToNearest;

    const formatPrice = (value) => {
        if (!value && value !== 0) return '--';
        return typeof value === 'number' ? value.toFixed(2) : value;
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Swing Points */}
            <Box
                sx={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #374151',
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}
            >
                <SectionHeader title="SWING POINTS" />
                <TableRow
                    label="Swing High"
                    value={formatPrice(swingHigh)}
                    valueColor="#22c55e"
                />
                <TableRow
                    label="Swing Low"
                    value={formatPrice(swingLow)}
                    valueColor="#ef4444"
                />
                <TableRow
                    label="Current Price"
                    value={formatPrice(currentPrice)}
                    valueColor="#fff"
                />
                {distanceToNearest !== undefined && (
                    <TableRow
                        label="Distance to Nearest"
                        value={`${distanceToNearest?.toFixed(2) || '--'} pips`}
                        valueColor="#fbbf24"
                    />
                )}
            </Box>

            {/* Fibonacci Levels */}
            <Box
                sx={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #374151',
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}
            >
                <SectionHeader title="FIBONACCI LEVELS" badge={nearestLevel ? `Nearest: ${nearestLevel}` : null} />

                {/* Column Headers - Improved visibility */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '6px 12px',
                        backgroundColor: 'rgba(55, 65, 81, 0.35)',
                        borderBottom: '1px solid rgba(55, 65, 81, 0.5)'
                    }}
                >
                    <Typography sx={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace', minWidth: '70px', fontWeight: 600 }}>
                        Level
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace', minWidth: '80px', textAlign: 'right', fontWeight: 600 }}>
                        Price
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace', minWidth: '70px', textAlign: 'right', fontWeight: 600 }}>
                        Status
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace', minWidth: '55px', textAlign: 'right', fontWeight: 600 }}>
                        Dist.
                    </Typography>
                </Box>

                {/* Fib Levels */}
                {levels.length > 0 ? (
                    levels.map((level, index) => (
                        <FibLevelRow
                            key={index}
                            level={level.level}
                            price={level.price}
                            status={level.status}
                            isNearest={level.level === nearestLevel}
                            currentPrice={currentPrice}
                        />
                    ))
                ) : (
                    <Box sx={{ padding: '12px', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '11px', color: '#6b7280' }}>
                            No Fibonacci levels available
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
});

FibonacciTable.displayName = 'FibonacciTable';

export default FibonacciTable;
